import type { Maybe } from '@adornis/base/utilTypes.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { wrapIntrospect } from '@adornis/introspect/server/introspect.js';
import { AdornisLLMRouter } from '@adornis/llm/server/llm-router.js';
import type { ChatCompletionMessageParam } from '@adornis/llm/server/token-js/index.js';
import { cleanHtmlContent } from '@source/backup/server/formatting-and-regex.js';
import { MailEntity } from '@source/mails/entities/mail.js';
import {
  RELEVANT_EMAIL_PROMPT,
  RELEVANT_EMAIL_SENDER_PROMPT,
} from '../prompts-and-default-texts/general-prompts_de.js';

export const checkEmailSenderRelevant = async (
  emailFromString: string,
  irrelevantPrefixes: string[],
  irrelevantSuffixes: string[],
) => {
  const relevantEmailSenderPrompt = RELEVANT_EMAIL_SENDER_PROMPT(irrelevantPrefixes, irrelevantSuffixes);
  const llmRouter = new AdornisLLMRouter({
    provider: 'openrouter',
    model: 'google/gemini-2.5-flash',
    max_tokens: 4000,
    temperature: 0,
  });
  const response = await wrapIntrospect('LLMNODE_checkEmailSenderRelevant', (messages: ChatCompletionMessageParam[]) =>
    llmRouter.chat.completions.create({ messages }),
  )([
    {
      role: 'system',
      content: relevantEmailSenderPrompt,
    },
    {
      role: 'user',
      content: emailFromString,
    },
  ]);
  return response.choices?.[0]?.message?.content ?? '';
};

export const checkEmailSpam = async (
  sender: string,
  subject: string | null | undefined,
  content: string | null | undefined,
) => {
  const userEntries = `Sender: ${sender}\nSubject: ${subject || '-'}\nContent: ${cleanHtmlContent(content || '-')}`;
  const llmRouter = new AdornisLLMRouter({
    provider: 'openrouter',
    model: 'google/gemini-2.5-flash',
    max_tokens: 4000,
    temperature: 0,
  });
  const response = await wrapIntrospect('LLMNODE_checkEmailSpam', (messages: ChatCompletionMessageParam[]) =>
    llmRouter.chat.completions.create({ messages }),
  )([
    {
      role: 'system',
      content: RELEVANT_EMAIL_PROMPT,
    },
    {
      role: 'user',
      content: userEntries,
    },
  ]);
  const result = response.choices?.[0]?.message?.content ?? '';
  if (result.includes('NEWSLETTER')) {
    return 'NEWSLETTER';
  } else if (result.includes('SPAM')) {
    return 'SPAM';
  } else if (result.includes('POTENTIAL_SUPPLIER')) {
    return 'POTENTIAL_SUPPLIER';
  } else if (result.includes('IT_SALES_LETTER')) {
    return 'IT_SALES_LETTER';
  } else if (result.includes('RELEVANT')) {
    return null;
  }
};

export async function getAllRelevantAndIrrelevantEmails() {
  // Update all other conversations with the same "from" sender
  const collection = await getCollection<MailEntity>(MailEntity._class);

  const conversations = await collection
    .find(
      {
        checkedAsIrrelevant: { $exists: true, $ne: null },
        from: { $exists: true, $ne: null },
      },
      { projection: { checkedAsIrrelevant: 1, from: 1 } },
    )
    .toArray();
  const relevantConversations = conversations.filter(conv => conv.checkedAsIrrelevant === false);
  const irrelevantConversations = conversations.filter(conv => conv.checkedAsIrrelevant === true);
  const allIrrelevantEmails = irrelevantConversations.map(conv => conv.from);
  const uniqueIrrelevantEmails = [...new Set(allIrrelevantEmails)];
  const relevantEmails = relevantConversations.map(conv => conv.from);
  const uniqueRelevantEmails = [...new Set(relevantEmails)];
  const irrelevantEmailSuffixes = uniqueIrrelevantEmails
    .map(email => {
      if (!email) {
        return;
      }
      const parts = email.split('@');
      if (parts.length > 1) {
        return parts[1];
      }
      return email;
    })
    .filter((suffix): suffix is string => Boolean(suffix));

  const irrelevantEmailPrefixes = uniqueIrrelevantEmails
    .map(email => {
      if (!email) {
        return;
      }
      const parts = email.split('@');
      if (parts.length > 1) {
        return parts[0];
      }
      return email;
    })
    .filter((prefix): prefix is string => Boolean(prefix));

  const relevantEmailSuffixes = uniqueRelevantEmails
    .map(email => {
      if (!email) {
        return;
      }
      const parts = email.split('@');
      if (parts.length > 1) {
        return parts[1];
      }
      return email;
    })
    .filter((suffix): suffix is string => Boolean(suffix));

  const relevantEmailPrefixes = uniqueRelevantEmails
    .map(email => {
      if (!email) {
        return;
      }
      const parts = email.split('@');
      if (parts.length > 1) {
        return parts[0];
      }
      return email;
    })
    .filter((prefix): prefix is string => Boolean(prefix));

  const uniqueIrrelevantEmailSuffixes = [...new Set(irrelevantEmailSuffixes)];
  const uniqueIrrelevantEmailPrefixes = [...new Set(irrelevantEmailPrefixes)];
  const uniqueRelevantEmailSuffixes = [...new Set(relevantEmailSuffixes)];
  const uniqueRelevantEmailPrefixes = [...new Set(relevantEmailPrefixes)];

  const upTo10RandomPrefixes = uniqueIrrelevantEmailPrefixes.sort(() => 0.5 - Math.random()).slice(0, 10);
  const upTo10RandomSuffixes = uniqueIrrelevantEmailSuffixes.sort(() => 0.5 - Math.random()).slice(0, 10);

  return {
    uniqueIrrelevantEmails: uniqueIrrelevantEmails,
    prefixes: uniqueIrrelevantEmailPrefixes,
    suffixes: uniqueIrrelevantEmailSuffixes,
    upTo10RandomPrefixes: upTo10RandomPrefixes,
    upTo10RandomSuffixes: upTo10RandomSuffixes,
    uniqueRelevantEmails: uniqueRelevantEmails,
    relevantPrefixes: uniqueRelevantEmailPrefixes,
    relevantSuffixes: uniqueRelevantEmailSuffixes,
  };
}

export async function markEmailsAsSuggestedAsIrrelevant() {
  const collection = await getCollection<MailEntity>(MailEntity._class);
  let conversations = await collection
    .find({
      $and: [
        {
          $or: [{ suggestedAsIrrelevant: { $exists: false } }, { suggestedAsIrrelevant: null }],
        },
        {
          $or: [{ checkedAsIrrelevant: { $exists: false } }, { checkedAsIrrelevant: null }],
        },
      ],
    })
    .toArray();
  conversations = conversations.splice(0, 100);
  const exampleEmailsRecord = await getAllRelevantAndIrrelevantEmails();
  if (!exampleEmailsRecord || typeof exampleEmailsRecord !== 'object') {
    return null;
  }
  const irrelevantEmailPrefixes: string[] = exampleEmailsRecord['upTo10RandomPrefixes'] ?? [];
  const irrelevantEmailSuffixes: string[] = exampleEmailsRecord['upTo10RandomSuffixes'] ?? [];
  for (const conversationItem of conversations) {
    const conversationId = conversationItem._id;
    const emailFrom = conversationItem.from;
    if (!emailFrom || emailFrom === null || emailFrom === undefined) {
      continue;
    }
    let llmResponse = await wrapIntrospect(
      'compareWithSavedIrrelevantAndCheckRelevant',
      compareWithSavedIrrelevantAndCheckRelevant,
    )({
      mailID: conversationId,
      emailFromString: emailFrom,
      upTo10RandomPrefixes: irrelevantEmailPrefixes,
      upTo10RandomSuffixes: irrelevantEmailSuffixes,
      relevantAndIrrelevantEmails: exampleEmailsRecord,
    });
    // stringify llmResponse if its an object
    if (typeof llmResponse === 'object') {
      llmResponse = JSON.stringify(llmResponse);
    }
    // check if llmRespone contains EMAIL_IRRELEVANT
    let irrelevantSuggested: null | boolean = null;
    if (llmResponse.includes('EMAIL_IRRELEVANT')) {
      irrelevantSuggested = true;
    } else if (llmResponse.includes('EMAIL_RELEVANT')) {
      irrelevantSuggested = false;
    }
    await collection.updateOne({ _id: conversationItem._id }, { $set: { suggestedAsIrrelevant: irrelevantSuggested } });
  }
  return true;
}

export async function compareWithSavedIrrelevantAndCheckRelevant(mailrecord: Record<string, any>) {
  const mailId = mailrecord.mailID;
  const emailFrom = mailrecord.emailFromString;
  const upTo10RandomPrefixes = mailrecord.upTo10RandomPrefixes;
  const upTo10RandomSuffixes = mailrecord.upTo10RandomSuffixes;
  return checkEmailSenderRelevant(emailFrom, upTo10RandomPrefixes, upTo10RandomSuffixes);
}

export const getMailEntityByPubSubMessageID = registerQuery({
  operationName: 'getMailEntityByPubSubMessageID',
  params: [
    {
      name: 'pubSubMessageId',
      type: () => String,
    },
  ],
  resolve: (messageID: string) => async (gqlFields: BaseQLSelectionSet<MailEntity>) => {
    const mailEntity = await (await getCollection(MailEntity._class)).findOne({ mailMessageId: messageID });
    return mailEntity as unknown as Maybe<MailEntity>;
  },
  type: () => MailEntity,
});
